/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { Auth } from '@ax/auth/src/Authentication/Auth.ts';
import { providerLoginApi } from '@ax/service';
import { ProviderLoginInput, ProviderLoginResponse } from '@ax/service';

type AuthenticationProps = {
  children?: React.ReactNode;
};

type ContextProps = {
  readonly login: ({ email, password }: ProviderLoginInput) => Promise<ProviderLoginResponse>;
  readonly logout: () => Promise<void>;
  readonly saveUserToken: (token: string) => Promise<void>;
  readonly currentUser: () => Promise<any>;
  readonly isLoggedIn: () => Promise<boolean>;
  readonly isLoggedInUserStatus: string;
};

const Authentication = createContext<ContextProps | null>(null);

export function AuthenticationProvider({ children }: AuthenticationProps) {
  const [isLoggedInUserStatus, setIsLoggedInUserStatus] = useState('LOADING');

  const isLoginCheck = async () => {
    const isLogin = await isLoggedIn();
    if (isLogin) {
      setIsLoggedInUserStatus('LOGIN_SUCCESS');
    } else {
      setIsLoggedInUserStatus('NOT_LOGGED_IN');
    }
  };

  useEffect(() => {
    void isLoginCheck();
  }, []);

  const login = async ({ email, password }: ProviderLoginInput) => {
    const res = await providerLoginApi({ email, password });
    return res;
  };

  const saveUserToken = (token: string) => {
    return new Promise<void>((resolve) => {
      Auth.saveUser(token);
      setIsLoggedInUserStatus('LOGIN_SUCCESS');
      resolve();
    });
  };

  const currentUser = () => {
    const isLogin = Auth.isLoggedIn();
    if (isLogin) {
      const user: any = Auth.currentUser();
      return user;
    }
    return undefined;
  };

  const isLoggedIn = () => {
    return new Promise<boolean>((resolve) => {
      const isLoggedIn = Auth.isLoggedIn();
      setIsLoggedInUserStatus('LOGIN_SUCCESS');
      resolve(isLoggedIn);
    });
  };

  const logout = () => {
    return new Promise<void>((resolve) => {
      Auth.logout();
      setIsLoggedInUserStatus('NOT_LOGGED_IN');
      resolve();
    });
  };

  return (
    <>
      <Authentication.Provider
        value={{ login, logout, saveUserToken, currentUser, isLoggedIn, isLoggedInUserStatus }}
      >
        {children}
      </Authentication.Provider>
    </>
  );
}

export const useAuthentication = () => {
  const context = useContext(Authentication);

  if (context === null) {
    throw new Error('useAuthentication context is not defined');
  }

  return context;
};
